import {
  BareComponent,
  ComponentType,
  TemplateStringProps
} from '@payflip/types/product-builder';

import React, { createElement, FC, FunctionComponent } from 'react';
import { Card } from '../../components/card';
import { Collapse } from '../../components/collapse';
import { Column } from '../../components/column';
import { Heading } from '../../components/heading';
import { Icon } from '../../components/icons';
import { LinkButton } from '../../components/link-button';
import { LinkCard } from '../../components/link-card';
import { List } from '../../components/list';
import { Paragraph } from '../../components/paragraph';
import { Row } from '../../components/row';
import { Toast } from '../../components/toast';

// import { CounterInputControl } from '../../../components/counter-input-control';
// import { DateRangeInput } from '../../../components/date-range-input';
// import { DocumentInput } from '../../../components/document-input';
// import { Footer } from '../components/footer';

import { Condition } from './components/condition';
import { BudgetHeader, ProductHeader } from './components/header';
import { HeaderImage } from './components/header-image';
import { HeaderTitle } from './components/header-title';
import { TemplateString } from './components/template-string/template-string';
// import { RHFCheckBoxInput } from '../components/rhf-checkbox-input';
// import { RHFCurrencyInput } from '../components/rhf-currency-input';
// import { RHFFormField } from '../components/rhf-form-field';
// import { RHFHiddenInput } from '../components/rhf-hidden-input';
// import { RHFTextInput } from '../components/rhf-text-input';
// import { SubmitButton } from '../components/submit-button';

//import { withFormContext } from './HOC/with-form-context';
import { withResolvedFormulas } from './HOC/with-resolved-formulas';
import { withSpacingProps } from './HOC/with-spacing-props';
import { withTemplatedString } from './HOC/with-templated-string';

type RendererScope = 'product' | 'budget';

export type FunctionalHOC = <T extends { [K in keyof T]: T[K] }>(
  Component: React.ComponentType<T>
) => React.FC<T>;

const gettKeysToComponentMap = (
  withPropResolver: FunctionalHOC,
  scope?: RendererScope
): Record<ComponentType, unknown> => {
  const keysToComponentMap: Record<ComponentType, unknown> = {
    [ComponentType.Paragraph]: withSpacingProps(Paragraph),
    [ComponentType.LinkButton]: withSpacingProps(
      withPropResolver(withTemplatedString(LinkButton))
    ),
    [ComponentType.LinkCard]: withSpacingProps(withPropResolver(LinkCard)),
    [ComponentType.Title]: withSpacingProps(Heading),
    [ComponentType.Separator]: () => <hr />,
    [ComponentType.Header]: scope === 'budget' ? BudgetHeader : ProductHeader,
    [ComponentType.HeaderAction]: null,
    [ComponentType.HeaderCost]: null,
    [ComponentType.Footer]: null,
    [ComponentType.Card]: withSpacingProps(withPropResolver(Card)),
    [ComponentType.Collapse]: withPropResolver(Collapse),
    [ComponentType.HeaderTitle]: (props: {
      name: string;
      position: 'left' | 'center' | 'right';
    }) => {
      return <HeaderTitle {...props} />;
    },
    [ComponentType.HeaderImage]: (props: {
      src: string;
      position: 'left' | 'center' | 'right';
    }) => {
      return <HeaderImage {...props} position="right" />;
    },
    [ComponentType.FooterNextButton]: null,
    [ComponentType.UrlInput]: null,
    [ComponentType.NumberInput]: null,
    [ComponentType.CurrencyInput]: null,
    [ComponentType.TextInput]: null,
    [ComponentType.TextAreaInput]: null,
    [ComponentType.SelectInput]: null,
    [ComponentType.RadioInput]: null,
    [ComponentType.BooleanInput]: null,
    [ComponentType.CheckboxInput]: null,
    [ComponentType.DateInput]: null,
    [ComponentType.CounterInput]: null,
    [ComponentType.Column]: withSpacingProps(Column),
    [ComponentType.Row]: withSpacingProps(Row),
    [ComponentType.List]: List,
    [ComponentType.Icon]: Icon,
    [ComponentType.Toast]: Toast,
    [ComponentType.DateRangeInput]: null,
    [ComponentType.DocumentInput]: null,
    [ComponentType.HiddenInput]: null,
    [ComponentType.FormField]: null,
    [ComponentType.UploadFile]: null,
    [ComponentType.TemplateString]: withPropResolver(
      withResolvedFormulas(
        TemplateString
      ) as FunctionComponent<TemplateStringProps>
    ),
    [ComponentType.Condition]: withPropResolver(Condition)
  };

  return keysToComponentMap;
};

export function renderer<TProps>(
  componentDefinition: BareComponent<TProps> | string,
  propsResolver: FunctionalHOC,
  scope?: RendererScope,
  key?: string | number
): JSX.Element | string | null {
  if (typeof componentDefinition === 'string') {
    return componentDefinition;
  }

  const component = gettKeysToComponentMap(propsResolver, scope)[
    componentDefinition.type
  ];

  const { children, props, type: _, ...rest } = componentDefinition;

  if (typeof component !== 'undefined') {
    if (typeof children === 'string') {
      return createElement(
        component as FC,
        { ...props, ...rest, key },
        children
      );
    }

    const realChildren = children
      ? Array.isArray(children)
        ? children
        : [children]
      : undefined;
    return createElement(
      component as FC,
      { ...props, ...rest, key },
      realChildren?.map((c, i) => renderer(c, propsResolver, scope, i))
    );
  }

  return <div>{`Missing component of type ${componentDefinition.type}`}</div>;
}
